import React, { useEffect, useState } from 'react'

import { AcornsIcon } from '@acorns/icons'
import {
  Drawer,
  DrawerActions,
  Heading2,
  OnSelectionListSelect,
  SelectionList,
  SelectionListOptionComponent,
  SelectionListRow,
} from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'

import SelectorImg from 'assets/images/spend/direct-deposit/selectorVisual.png'

import { DirectDepositMethod } from '../Interstitial'
import { setupCTA, setupContainer, setupExit } from '../segment'
import {
  CustomRowContent,
  LearnMoreLink,
  StyledBadge,
  StyledChevron,
  StyledHeading2,
  StyledSelectorImg,
  Wrapper,
} from './DrawerComponents'

const PAYROLL_PROVIDER_FAQ_URL = '/grow/2ba02afe-0a7b-4d73-a416-9e8bb0f84514'
export const HIDE_ACTIONSTRIP = '?hideActionStrip=true'

export type Props = {
  showSetUpDrawer: boolean
  closeSetUpDrawer: () => void
  setShowDisclosureDrawer: (showDrawer: boolean) => void
  goToDirectDepositForm: () => void
}

export const SetUpDrawer: React.FC<Props> = (props: Props) => {
  const analytics = useAnalyticsClient()
  const { getRemoteString } = useRemoteStrings()

  const learnMoreLocation = {
    pathname: PAYROLL_PROVIDER_FAQ_URL,
    search: HIDE_ACTIONSTRIP,
  }

  useEffect(() => {
    if (props.showSetUpDrawer) {
      analytics.track('Screen Viewed', setupContainer())
    }
  }, [props.showSetUpDrawer])

  const CustomSelectionListRow: SelectionListOptionComponent = (props) => {
    return (
      <SelectionListRow {...props} alignCheckmark={'top'}>
        <CustomRowContent>
          <div>
            <StyledBadge type={'primary'}>
              {getRemoteString({
                key: 'spend.direct_deposit.drawer.selection.payroll_provider.pill.title',
              })}
            </StyledBadge>
          </div>
          <StyledHeading2>
            {getRemoteString({
              key: 'spend.direct_deposit.drawer.selection.payroll_provider.title',
            })}
          </StyledHeading2>
          <LearnMoreLink
            to={learnMoreLocation}
            key={PAYROLL_PROVIDER_FAQ_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            {getRemoteString({
              key: 'spend.direct_deposit.drawer.selection.promo.link',
            })}
            <StyledChevron
              icon={AcornsIcon.Icon.DisclosureRight}
              width="12px"
            />
          </LearnMoreLink>
          <StyledSelectorImg src={SelectorImg} />
        </CustomRowContent>
      </SelectionListRow>
    )
  }

  const customComponentOptions = [
    {
      label: 'Pinwheel Integration',
      value: DirectDepositMethod.Pinwheel,
      component: CustomSelectionListRow,
    },
    {
      label: (
        <Heading2>
          {getRemoteString({
            key: 'spend.direct_deposit.drawer.selection.deposit_form.title',
          })}
        </Heading2>
      ),
      value: DirectDepositMethod.Manual,
    },
  ]

  const [directDepositMethod, setDirectDepositMethod] = useState(
    customComponentOptions[0].value,
  )

  const SelectionListCustomComponent = () => {
    const handleSelect: OnSelectionListSelect = (option) => {
      setDirectDepositMethod(option.value as DirectDepositMethod)
    }

    return (
      <Wrapper>
        <SelectionList
          options={customComponentOptions}
          value={directDepositMethod}
          onSelect={handleSelect}
          rowStyle={'card'}
        />
      </Wrapper>
    )
  }

  const onNextButtonPress = () => {
    if (directDepositMethod === DirectDepositMethod.Pinwheel) {
      analytics.track('Button Tapped', setupCTA(directDepositMethod))
      props.setShowDisclosureDrawer(true)
      props.closeSetUpDrawer()
    } else {
      props.closeSetUpDrawer()
      props.goToDirectDepositForm()
    }
  }

  const closeDrawerReturnToDefault = () => {
    analytics.track('Button Tapped', setupExit())
    props.closeSetUpDrawer()
    setDirectDepositMethod(customComponentOptions[0].value)
  }

  return (
    <Drawer
      isOpen={props.showSetUpDrawer}
      onClose={closeDrawerReturnToDefault}
      title={getRemoteString({
        key: 'spend.direct_deposit.drawer.title',
      })}
    >
      <SelectionListCustomComponent />
      <DrawerActions
        actions={[
          {
            label: getRemoteString({
              key: 'spend.direct_deposit.drawer.cta',
            }),
            handler: onNextButtonPress,
            testId: 'next_button',
          },
        ]}
      />
    </Drawer>
  )
}
